import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Image } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useCart } from '../../context/CartContext';
import { useToastContext } from '../../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import ImageViewer from './ImageViewer';
import { api } from '../../services/api';

const CardContainer = styled.div<{ isVertical?: boolean }>`
  position: relative;
  border-radius: 12px;
  overflow: visible; /* Cambiado de 'hidden' a 'visible' para mostrar el menú */
  transition: transform 0.2s, box-shadow 0.2s;
  grid-row-end: ${props => props.isVertical ? 'span 2' : 'auto'};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ImageWrapper = styled.div<{ isVertical?: boolean }>`
  position: relative;
  width: 100%;
  padding-top: ${props => props.isVertical ? '177.78%' : '100%'}; /* 16:9 (177.78%) o 1:1 (100%) Aspect ratio */
  overflow: hidden;
  border-radius: 12px;
`;

const MagnifyOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: 12px;
  z-index: 5;
  
  ${CardContainer}:hover & {
    opacity: 1;
  }
`;

const DeleteIconContainer = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
`;

const MagnifyIcon = styled.button`
  background-color: rgba(255, 255, 255, 0.85);
  border: none;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s, background-color 0.2s;
  
  &:hover {
    background-color: white;
    transform: scale(1.1);
  }
  
  svg {
    width: 24px;
    height: 24px;
    fill: #333;
  }
`;

const DeleteIcon = styled.button<{ isDeleted?: boolean }>`
  background-color: ${props => props.isDeleted ? 'rgba(255, 0, 0, 0.9)' : 'rgba(0, 0, 0, 0.85)'};
  border: none;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s, background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.isDeleted ? 'rgba(255, 0, 0, 1)' : 'rgba(0, 0, 0, 1)'};
    transform: scale(1.1);
  }
  
  span {
    font-size: 32px;
    color: white;
  }
`;

// Placeholder para mostrar mientras se carga la imagen
const ImagePlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.colors.background.tertiary || '#2a2a2a'};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::after {
    content: '';
    width: 30px;
    height: 30px;
    border: 3px solid rgba(255, 255, 255, 0.2);
    border-top-color: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const StyledImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s, opacity 0.3s;
  border-radius: 12px;
  will-change: transform; /* Optimización de rendimiento */
  
  ${CardContainer}:hover & {
    transform: scale(1.03);
  }
`;

const ContextMenu = styled.div<{ show: boolean }>`
  display: ${props => (props.show ? 'block' : 'none')};
  position: absolute; /* Cambiado de 'fixed' a 'absolute' */
  z-index: 1000;
  left: 50%; /* Centrado horizontalmente */
  top: 50%; /* Centrado verticalmente */
  transform: translate(-50%, -50%); /* Asegura el centrado perfecto */
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  padding: 0.5rem 0;
  min-width: 180px; /* Ajustado para el tamaño de iconos normal */
  cursor: default; /* Evitar que se muestre el cursor de flecha al pasar por encima */
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0.75rem;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s, color 0.2s;
  
  &:hover {
    background-color: #1E88E5; /* Azul Material Design */
    color: white; /* Texto blanco para mejor contraste */
    
    svg {
      fill: white;
    }
  }
  
  svg {
    margin-right: 8px;
    width: 16px; /* A la mitad del tamaño actual (32px ÷ 2 = 16px) */
    height: 16px; /* A la mitad del tamaño actual (32px ÷ 2 = 16px) */
    transition: fill 0.2s;
  }
`;

interface ImageCardProps {
  image: Image;
  showContextMenu?: boolean;
}

const ImageCard: React.FC<ImageCardProps> = ({ image, showContextMenu = true }) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [viewerVisible, setViewerVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  
  // Debug info
  console.log('Rendering image:', image);
  
  // Determinar si la imagen debe mostrarse en formato vertical
  const isVertical = image.height > image.width * 1.2; // Si la altura es más de 1.2 veces el ancho, la consideramos vertical
  
  const { user } = useAuth();
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();
  const { addToCart, isInCart } = useCart();
  const { showWarning } = useToastContext();
  const navigate = useNavigate();

  const handleContextMenu = (e: React.MouseEvent) => {
    if (showContextMenu) {
      e.preventDefault();
      e.stopPropagation(); // Detener la propagación del evento
      
      // Cerrar cualquier otro menú contextual que pudiera estar abierto
      const customEvent = new CustomEvent('closeAllContextMenus');
      document.dispatchEvent(customEvent);
      
      // Ya no necesitamos posicionar el menú por las coordenadas del cursor
      // porque vamos a mostrarlo centrado en la imagen
      
      // Mostrar el menú inmediatamente
      setMenuVisible(true);
    }
  };
  
  // Función para manejar clics en la imagen
  const handleImageClick = (e: React.MouseEvent) => {
    // Si hay un menú contextual visible, cerrar el menú
    if (menuVisible) {
      // Verificar si el clic no fue dentro del menú
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        e.preventDefault();
        e.stopPropagation();
        closeMenu();
      }
    }
  };

  const handleFavoriteClick = () => {
    if (!user) {
      showWarning('Haz login para agregar a favoritos');
      setMenuVisible(false);
      return;
    }
    
    if (isFavorite(image.id)) {
      removeFromFavorites(image.id);
    } else {
      addToFavorites(image);
    }
    setMenuVisible(false);
  };

  const handleCartClick = () => {
    if (!user) {
      showWarning('Haz login para agregar al carrito');
      setMenuVisible(false);
      return;
    }
    
    addToCart(image);
    setMenuVisible(false);
  };
  
  // Función para cerrar este menú contextual
  const closeMenu = () => {
    setMenuVisible(false);
  };

  // Referencia al menú contextual para detectar clics fuera de él
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Manejar clic fuera del menú contextual
  const handleClickOutside = (e: MouseEvent) => {
    // Si el menú está visible y el clic no fue dentro del menú, cerrarlo
    if (menuVisible && menuRef.current && !menuRef.current.contains(e.target as Node)) {
      console.log('Clic fuera del menú contextual, cerrando');
      closeMenu();
    }
  };
  
  // Escuchar evento personalizado para cerrar todos los menús contextuales
  const handleCloseAllMenus = () => {
    closeMenu();
  };

  // Configurar event listeners
  useEffect(() => {
    // Solo agregar event listeners si el menú está visible
    if (menuVisible) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('closeAllContextMenus', handleCloseAllMenus);
      
      // Cleanup
      return () => {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('closeAllContextMenus', handleCloseAllMenus);
      };
    }
    
    return undefined;
  }, [menuVisible]);

  // Función para abrir el visor de imágenes
  const handleViewerOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Cerrar cualquier otro menú contextual que pudiera estar abierto
    const customEvent = new CustomEvent('closeAllContextMenus');
    document.dispatchEvent(customEvent);
    
    // Forzar que se cierre el menú contextual si está abierto
    if (menuVisible) {
      setMenuVisible(false);
    }
    
    // Añadir un contenedor para el modal si no existe
    if (!document.getElementById('modal-root')) {
      const modalRoot = document.createElement('div');
      modalRoot.id = 'modal-root';
      document.body.appendChild(modalRoot);
    }
    
    // Abrir el visor
    setViewerVisible(true);
  };

  // Función para cerrar el visor de imágenes
  const handleViewerClose = () => {
    setViewerVisible(false);
  };

  // Estado para controlar si la imagen fue eliminada
  const [isDeleted, setIsDeleted] = useState(false);

  // Función para manejar la eliminación de imagen (solo para admins)
  const handleDeleteImage = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user || (user.email !== 'javajmc@gmail.com' && user.email !== 'ilyana.vasquez@gmail.com')) {
      showWarning('No tienes permisos para eliminar imágenes');
      return;
    }

    try {
      await api.delete(`/canvamanager/imgs/${image.id}`, {
        headers: {
          'X-User-Email': user.email
        }
      });
      setIsDeleted(true);
      showWarning('Imagen eliminada exitosamente');
    } catch (error) {
      console.error('Error eliminando imagen:', error);
      showWarning('Error al eliminar la imagen');
    }
  };

  // Estado para controlar la carga de la imagen
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [fromCache, setFromCache] = useState(false);

  // Importación de servicios de caché
  // Nota: Para evitar circular imports, importamos dinámicamente 
  // utilizando React.useEffect más abajo
  
  useEffect(() => {
    // Verificar si la imagen está en caché al montar el componente
    const checkCache = async () => {
      try {
        // Importación dinámica para evitar problemas de dependencia circular
        const { imageCache } = await import('../../services/imageCache');
        
        // Inicializar la caché si aún no está inicializada
        await imageCache.init();
        
        // Comprobar si la imagen está en caché
        const isCached = await imageCache.isImageCached(image.url);
        setFromCache(isCached);
        
        // Guardar metadatos de la imagen
        await imageCache.saveImageMetadata({
          id: image.id,
          url: image.url,
          width: image.width,
          height: image.height,
          title: image.title
        });
      } catch (error) {
        console.error('Error checking image cache:', error);
      }
    };
    
    checkCache();
  }, [image.id, image.url, image.width, image.height, image.title]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setImageError(true);
    // Si hay error al cargar la imagen, mostrar imagen de fallback
    const target = e.target as HTMLImageElement;
    target.src = 'https://via.placeholder.com/800x800?text=Imagen+no+disponible';
    target.onerror = null; // Prevenir bucle infinito
  };

  return (
    <CardContainer 
      className="CardContainer" 
      ref={cardRef} 
      onContextMenu={handleContextMenu} 
      onClick={handleImageClick}
      isVertical={isVertical}>
      <ImageWrapper isVertical={isVertical}>
        {!imageLoaded && !imageError && <ImagePlaceholder />}
        <StyledImage 
          className={`StyledImage ${fromCache ? 'from-cache' : ''}`}
          src={image.url} 
          alt={image.title || `Imagen ${image.id}`} 
          loading="lazy" 
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{ 
            opacity: imageLoaded ? 1 : 0,
            // Añadir un sutil borde azul a las imágenes cargadas desde caché
            // Solo para depuración, se puede quitar en producción
            borderBottom: fromCache ? '2px solid rgba(110, 158, 255, 0.5)' : 'none'
          }}
        />
        <MagnifyOverlay className="MagnifyOverlay">
          <MagnifyIcon onClick={handleViewerOpen} aria-label="Ver imagen completa">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
          </MagnifyIcon>
        </MagnifyOverlay>
        
        {/* Botón de eliminar siempre visible para usuarios admin */}
        {(user?.email === 'javajmc@gmail.com' || user?.email === 'ilyana.vasquez@gmail.com') && (
          <DeleteIconContainer>
            <DeleteIcon 
              onClick={handleDeleteImage} 
              aria-label="Eliminar imagen"
              isDeleted={isDeleted}
            >
              <span>🗑️</span>
            </DeleteIcon>
          </DeleteIconContainer>
        )}
      </ImageWrapper>
      
      <ContextMenu 
        ref={menuRef} 
        show={menuVisible} 
        data-context-menu="true"
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleFavoriteClick}>
          {isFavorite(image.id) ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FF5252">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
              Quitar de favoritos
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#757575">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
              Agregar a favoritos
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleCartClick}>
          {isInCart(image.id) ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FFC107">
                <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
              Ya está en el carrito
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#757575">
                <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
              Agregar al carrito
            </>
          )}
        </MenuItem>
      </ContextMenu>
      
      {viewerVisible && (
        <ImageViewer image={image} onClose={handleViewerClose} />
      )}
    </CardContainer>
  );
};

export default ImageCard;